import React from 'react';

// Line icons in the same style as the album filter icon: 24px grid, round caps, drawn in currentColor.
// They size with the surrounding font-size (1em), so a button's fontSize controls the icon.
const Icon = ({ filled, strokeWidth = 2.5, rotate, children }) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ display: 'block', transform: rotate ? `rotate(${rotate}deg)` : undefined }}>
    {children}
  </svg>
);

export const PinIcon = ({ filled }) => (
  <Icon filled={filled} strokeWidth={2}>
    <path d="M12 17v5" />
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
  </Icon>
);

// Song row controls in edit mode.
export const GripIcon = () => (
  <Icon filled strokeWidth={0}>
    {[7, 12, 17].map((y) => [9, 15].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.6" />))}
  </Icon>
);

export const ChevronUpIcon = () => (
  <Icon strokeWidth={2.25}><polyline points="6 15 12 9 18 15" /></Icon>
);

export const ChevronDownIcon = () => (
  <Icon strokeWidth={2.25}><polyline points="6 9 12 15 18 9" /></Icon>
);

export const MicIcon = () => (
  <Icon strokeWidth={2}>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="22" />
  </Icon>
);

export const TrashIcon = () => (
  <Icon strokeWidth={2}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </Icon>
);

// Saved albums: outline to save, filled when it's in the library.
export const BookmarkIcon = ({ filled }) => (
  <Icon filled={filled} strokeWidth={2.25}>
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </Icon>
);

export const PrevIcon = () => (
  <Icon filled>
    <polygon points="19 20 9 12 19 4 19 20" />
    <line x1="5" y1="19" x2="5" y2="5" />
  </Icon>
);

export const NextIcon = () => (
  <Icon filled>
    <polygon points="5 4 15 12 5 20 5 4" />
    <line x1="19" y1="5" x2="19" y2="19" />
  </Icon>
);

export const PlayIcon = () => (
  <Icon filled>
    <polygon points="6 3 20 12 6 21 6 3" />
  </Icon>
);

export const PauseIcon = () => (
  <Icon filled>
    <rect x="14" y="4" width="4" height="16" rx="1" />
    <rect x="6" y="4" width="4" height="16" rx="1" />
  </Icon>
);

export const LoopIcon = ({ one }) => (
  <Icon strokeWidth={2.25}>
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    {one && <path d="M11 10h1v4" />}
  </Icon>
);

// Double chevron: the sidebar collapse/expand toggle. The player toggle is the same icon rotated.
export const ChevronsIcon = ({ dir = 'left', rotate }) => (
  <Icon rotate={rotate}>
    {dir === 'left' ? (
      <React.Fragment>
        <path d="m11 17-5-5 5-5" />
        <path d="m18 17-5-5 5-5" />
      </React.Fragment>
    ) : (
      <React.Fragment>
        <path d="m6 17 5-5-5-5" />
        <path d="m13 17 5-5-5-5" />
      </React.Fragment>
    )}
  </Icon>
);

export const EditIcon = () => (
  <Icon strokeWidth={2.25}>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </Icon>
);

export const PlusIcon = () => (
  <Icon strokeWidth={2.25}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Icon>
);

export const CheckIcon = () => (
  <Icon>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);

export const AnalyticsIcon = () => (
  <Icon strokeWidth={2.25}>
    <line x1="6" y1="20" x2="6" y2="12" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="18" y1="20" x2="18" y2="9" />
  </Icon>
);

export const ShareIcon = () => (
  <Icon strokeWidth={2.25}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </Icon>
);

// Social platforms, drawn in the same line style as the rest of the set.
export const InstagramIcon = () => (
  <Icon strokeWidth={2.25}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </Icon>
);

export const SpotifyIcon = () => (
  <Icon strokeWidth={2.25}>
    <circle cx="12" cy="12" r="10" />
    <path d="M7 9.2c3.3-1 7.2-.7 10 1" />
    <path d="M7.6 12.6c2.7-.8 5.6-.5 8 .9" />
    <path d="M8.3 15.7c2.1-.6 4.2-.4 6.1.7" />
  </Icon>
);

export const GlobeIcon = () => (
  <Icon strokeWidth={2.25}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </Icon>
);

export const YouTubeIcon = () => (
  <Icon strokeWidth={2.25}>
    <path d="M22.5 7.2a2.5 2.5 0 0 0-1.8-1.8C19.2 5 12 5 12 5s-7.2 0-8.7.4A2.5 2.5 0 0 0 1.5 7.2C1.1 8.7 1.1 12 1.1 12s0 3.3.4 4.8a2.5 2.5 0 0 0 1.8 1.8C4.8 19 12 19 12 19s7.2 0 8.7-.4a2.5 2.5 0 0 0 1.8-1.8c.4-1.5.4-4.8.4-4.8s0-3.3-.4-4.8z" />
    <polygon points="10 15 15 12 10 9 10 15" />
  </Icon>
);

export const AppleMusicIcon = () => (
  <Icon strokeWidth={2.25}>
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </Icon>
);
